import classNames from 'classnames';
import { CSSProperties, forwardRef, ReactNode } from 'react';
import styles from './grid-layout.module.scss';

export interface GridLayoutProps {
  /** Indicates the color of the background of the grid */
  backgroundColor?: 'default' | 'surface';
  /** Indicates the color of the grid borders */
  borderColor?: 'default' | 'neutral';
  /** The children to render in the grid layout. */
  children: ReactNode;
  /** The class name to apply */
  className?: string;
  /** Whether the first and second items should stretch when alone */
  columnType?: 'stretch' | 'static';
  /** The three column threshold */
  threeColumnAt?: number;
  /** The two column threshold */
  twoColumnAt?: number;
}

const GridLayout = forwardRef<HTMLDivElement, GridLayoutProps>(
  (
    {
      backgroundColor,
      children,
      className,
      columnType = 'stretch',
      threeColumnAt,
      twoColumnAt,
      borderColor = 'default',
    },
    ref,
  ) => {
    const gridClass = `grid-${twoColumnAt}-${threeColumnAt}`;

    const style = borderColor
      ? ({
          '--grid-background-color': `var(--background-${backgroundColor})`,
          '--grid-border-color': `var(--border-${borderColor})`,
        } as CSSProperties)
      : undefined;

    return (
      <div
        ref={ref}
        className={classNames(styles.component, styles[gridClass], className, {
          [styles.isStatic]: columnType === 'static',
          [styles.isStretch]: columnType === 'stretch',
        })}
        style={style}
      >
        <div
          className={classNames(styles.content, {
            [styles.hasBackgroundColor]: !!backgroundColor,
          })}
        >
          {children}
        </div>
      </div>
    );
  },
);

GridLayout.displayName = 'GridLayout';

export default GridLayout;
